// =============================================================================
// PRODUCT INFO COLUMN — Horizon `Component` port of the PDP main-product info-column wiring
// (sections/main-product.liquid `.product-info` wrapper). Wave driver-swap: reparents the behaviour
// of the bundled src/js/objects/global/product-info.js (blob e330226) onto Horizon's `Component`
// base class, loaded as native ESM via the `@theme/*` importmap (snippets/horizon-importmap.liquid)
// rather than the webpack src/js bundle.
//
// VERBATIM TRANSPLANT (owner directive): the six selector constants and the five functions below —
// formatMoneySafe / getQty / renderTotal / selectVariant / onChange — are copied BYTE-IDENTICAL
// from the old module (its lines 23–91), including their internal comments and querySelector
// lookups. Only the boot/binding ARCHITECTURE changed:
//
//   OLD: self-initialising side-effect module → window.__productInfoInit global singleton →
//        document-level 'change' + 'input' delegation → one boot-time seed of every root.
//   NEW: <product-info-column class="product-info"> custom element on the section-owned wrapper →
//        per-instance 'change' + 'input' listeners on the element itself (same single shared
//        handler, bubble phase, no options object) → per-instance seed in connectedCallback.
//        The global singleton is retired by the Component lifecycle: a per-node #bound guard +
//        the customElements.define guard below (quick-add.js reinjects script tags on modal
//        injection, so double evaluation is plausible; module URLs also dedupe).
//
// Behaviour deliberately PRESERVED (parity beats fix — flagged, not changed):
//   • A radio click fires BOTH `input` and `change` → selectVariant runs twice per click
//     (idempotent). Both event types are load-bearing: qty TYPING only fires `input`; Motto's
//     <quantity-input> ± buttons only fire a native bubbling `change` (no pubsub for qty).
//   • The seed rewrites the server-rendered `money_without_currency` text with formatMoney output
//     (adds the currency symbol) — existing behaviour, kept.
//   • Hidden-id mirror (MOTTO CONTRACT, byte-identical): value = the radio's variant-id string,
//     `disabled = false` UNCONDITIONALLY on selection — safe because unavailable variants render
//     their radio `disabled` so they can never fire change. Vendored assets/product-form.js
//     (untouched) reads this input via canSubmitToCart() and `new FormData(this.form)`
//     (a disabled hidden input is EXCLUDED from FormData) — no <variant-radios>/picker renders in
//     this markup, so the hidden input's value is the SOLE variant source of the ATC POST.
//   • The typeof window.formatMoney guard stays (assets/global.js → theme.moneyFormat): module
//     timing changes from defer-bundle to type=module — the guard is the safety net, with the
//     same plain `$` fallback.
//   • `.is-selected` state class KEPT (already namespaced; consumers: _product-info.scss size-card
//     rules + the server seed in blocks/product-info-size.liquid).
//
// REFS: the two block files (blocks/product-info-size.liquid + blocks/product-info-buy.liquid,
// both `tag: null` → bare, no .shopify-block wrapper) mark the wired elements with ref=
// attributes; the base class keeps this.refs MutationObserver-synced across Theme Editor
// re-renders and quick-view modal injection. The transplanted function bodies keep their own
// verbatim root-scoped querySelector lookups (parity beats refs), so the refs are held,
// documented handles — not the logic's lookup path.
//
// NO SUBSCRIBE & SAVE LOGIC LIVES HERE: the "ORDER TYPE" / S&S selling-plan UI is intentionally
// excluded from scope (see blocks/product-info-buy.liquid doc + the comment in
// sections/main-product.liquid where it would sit); the only money logic in this module is
// total = data-unit-price × qty, formatted by formatMoney. The real S&S savings logic is
// PROTECTED app/Loop territory (src/js/objects/loop-widget/loop-widget.js) — not this file's.
//
// This element CONTAINS the section's merchant `content_for "blocks"` slot (the PROTECTED Loop
// app widget + subscribe_benefit blocks render inside it) and, in quick-view, the view-details
// link — containment only; no inner wrappers were restructured.
//
// UNTRACKED new file (alongside assets/component.js + assets/utilities.js) — re-verify it exists
// before any push; a parallel agent's git op on assets/ can remove it.
// =============================================================================
import { Component } from '@theme/component';

// ---- TRANSPLANTED VERBATIM from src/js/objects/global/product-info.js (lines 23–91) ------------

const ROOT = '.product-info';
const SIZE_INPUT = '.product-info__size-input';
const PRICE = '.product-info__add-price';
const QTY = '.product-info__qty-input';
const HIDDEN_ID = 'input[name="id"]';
const ADD_BTN = '.product-info__add';
const PAYMENT = '.product-info__payment-amount';

function formatMoneySafe(cents) {
  if (typeof window.formatMoney === 'function')
    return window.formatMoney(cents);
  return `$${(cents / 100).toFixed(2)}`;
}

function getQty(root) {
  const qtyInput = root.querySelector(QTY);
  let qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
  if (Number.isNaN(qty) || qty < 1) qty = 1;
  return qty;
}

function renderTotal(root) {
  const priceEl = root.querySelector(PRICE);
  if (!priceEl) return;
  const unit = parseInt(priceEl.dataset.unitPrice, 10);
  if (Number.isNaN(unit)) return;
  priceEl.textContent = formatMoneySafe(unit * getQty(root));
}

// UBB-370: rewrite ?variant= in the URL when a size card is selected so the address stays
// shareable and ad deep-links reproduce correctly on copy/paste. Preserves all existing params
// (UTM etc.) via URLSearchParams — only the `variant` key is set/updated. Skips quickview
// modals and any non-PDP context so the main page URL is never clobbered from a modal.
function updateVariantURL(root, variantId) {
  if (!window.location.pathname.match(/\/products\//)) return;
  if (root.closest('dialog, [data-modal], quick-add-modal, .quickview')) return;
  const params = new URLSearchParams(window.location.search);
  params.set('variant', String(variantId));
  window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}${window.location.hash}`);
}

function selectVariant(root, input) {
  // selected card styling (Espresso media border + filled radio)
  root.querySelectorAll('.product-info__size-card').forEach((card) => {
    card.classList.toggle('is-selected', card.contains(input));
  });

  // mirror the variant id into the buy form's hidden input (the source product-form.js reads)
  const hidden = root.querySelector(HIDDEN_ID);
  if (hidden) {
    hidden.value = input.value;
    hidden.disabled = false;
  }

  // refresh the live-total unit price from the chosen variant
  const priceEl = root.querySelector(PRICE);
  if (priceEl && input.dataset.price) {
    priceEl.dataset.unitPrice = input.dataset.price;
  }

  // UBB: keep the Afterpay "4 × X" instalment in sync with the variant. The server renders it once via
  // `price | divided_by: 4 | money_without_currency`; mirror that here — integer-truncated cents (Liquid
  // divided_by floors) + the shop money format with the leading currency symbol stripped — since
  // selectVariant is the only post-load variant hook.
  const payEl = root.querySelector(PAYMENT);
  if (payEl && input.dataset.price) {
    const divisor = parseInt(payEl.dataset.divisor, 10) || 4;
    const per = Math.floor(parseInt(input.dataset.price, 10) / divisor);
    if (!Number.isNaN(per)) payEl.textContent = formatMoneySafe(per).replace(/^\D+/, '');
  }

  // reflect availability on the ADD TO CART button
  const addBtn = root.querySelector(ADD_BTN);
  if (addBtn) {
    const productForm = addBtn.closest('product-form');
    const isSingleItemLocked =
      productForm?.dataset.singleCartItem === 'true' &&
      productForm?.dataset.alreadyInCart === 'true';
    addBtn.disabled = isSingleItemLocked || input.dataset.available === 'false';
  }

  renderTotal(root);

  // UBB-370: keep URL in sync with the selected variant
  updateVariantURL(root, input.value);
}

function onChange(event) {
  const target = event.target;
  if (!target || typeof target.matches !== 'function') return;

  if (target.matches(SIZE_INPUT)) {
    const root = target.closest(ROOT);
    if (root) selectVariant(root, target);
    return;
  }

  if (target.matches(QTY)) {
    const root = target.closest(ROOT);
    if (root) renderTotal(root);
  }
}

// ---- End of verbatim transplant -----------------------------------------------------------------

/**
 * The PDP main-product info-column custom element (`<product-info-column class="product-info">`).
 *
 * Held, MutationObserver-synced handles for the wired elements (see the REFS note above — the
 * transplanted logic keeps its own verbatim lookups). All optional: NO requiredRefs, because the
 * old module silently no-oped on absent elements (e.g. the price span does not render on a
 * sold-out product; the whole size fieldset collapses on an all-bundle product) — MissingRefError
 * would throw where the old module stayed quiet.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement[]} [sizeInputs] - the size radio inputs (blocks/product-info-size.liquid).
 * @property {HTMLElement[]} [sizeCards] - the size radio-card <label>s (is-selected lives here).
 * @property {HTMLInputElement} [hiddenId] - the buy form's hidden [name="id"] (blocks/product-info-buy.liquid).
 * @property {HTMLInputElement} [qtyInput] - the <quantity-input> stepper's number input.
 * @property {HTMLElement} [addPrice] - the ADD TO CART live-total span (absent when sold out).
 * @property {HTMLButtonElement} [addButton] - the ADD TO CART submit button.
 *
 * @extends {Component<Refs>}
 */
export class ProductInfoColumn extends Component {
  // Per-instance element-listener guard — retires the old window.__productInfoInit GLOBAL
  // singleton: the change/input pair binds exactly once per node (element listeners survive a
  // disconnect/reconnect of the SAME node) and dies with the node when a re-render replaces it.
  #bound = false;

  connectedCallback() {
    super.connectedCallback(); // populates this.refs (synchronous) + wires the base MutationObserver

    if (!this.#bound) {
      this.#bound = true;
      // Per-instance replacement for the old document-level delegation: the SAME single shared
      // handler on BOTH event types, bubble phase, no options object. onChange's
      // `target.closest(ROOT)` resolves to THIS element (the tag carries class="product-info"),
      // so the verbatim handler runs unchanged.
      this.addEventListener('change', onChange);
      this.addEventListener('input', onChange);
    }

    // Boot-seed parity, per instance: the old module seeded every present root once at bundle
    // boot; the lifecycle seeds each instance on (re)connect instead — which is also what
    // replaces the old document-delegation resilience for Theme Editor re-renders and quick-add
    // modal injection (quick-add.js reinjects script tags; the element upgrades on insertion).
    renderTotal(this);

    // UBB-370: seed the URL with the server-selected variant on load so the address is
    // immediately shareable without requiring the user to click a different size first.
    const checkedInput = this.querySelector(`${SIZE_INPUT}:checked`);
    if (checkedInput) updateVariantURL(this, checkedInput.value);
  }
}

if (!customElements.get('product-info-column')) {
  customElements.define('product-info-column', ProductInfoColumn);
}
